import { useState } from 'react';
import { Eye, EyeOff, Magnet } from 'lucide-react';
import { useGrid } from '../../context/GridContext';

/**
 * GridControls - Quick-access controls for grid settings
 *
 * Features:
 * - Grid visibility toggle
 * - Snap-to-grid toggle
 * - Grid size selector (10, 25, 50, 100 cm)
 *
 * Design: Fixed position bottom-left, offset by sidebar width (280px + 16px).
 */
const GridControls = () => {
  const {
    gridEnabled,
    snapEnabled,
    gridSize,
    toggleGrid,
    toggleSnap,
    setGridSize,
  } = useGrid();

  const [gridButtonHover, setGridButtonHover] = useState(false);
  const [snapButtonHover, setSnapButtonHover] = useState(false);

  // Both tabs use 280px sidebar; offset = 280px + 16px padding
  const leftPosition = '296px';

  const buttonBaseStyle = {
    width: '44px',
    height: '44px',
    border: '1px solid #E5E7EB',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  };

  // Removed unused styles - keeping for future hover/active state implementation
  // const buttonActiveStyle = {
  //   backgroundColor: '#D1FAE5', // Emerald-100
  //   borderColor: '#10B981', // Emerald-500
  // };
  // const buttonHoverStyle = {
  //   backgroundColor: '#F3F4F6', // Gray-100
  // };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '16px',
        left: leftPosition,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        backgroundColor: 'white',
        padding: '8px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        zIndex: 10,
      }}
    >
      {/* Grid visibility toggle */}
      <button
        onClick={toggleGrid}
        onMouseEnter={() => setGridButtonHover(true)}
        onMouseLeave={() => setGridButtonHover(false)}
        style={{
          ...buttonBaseStyle,
          borderColor: gridEnabled ? '#10B981' : '#E5E7EB',  // Emerald-500 or Gray-200
          backgroundColor: gridButtonHover
            ? (gridEnabled ? '#A7F3D0' : '#F3F4F6')  // Hover: Emerald-200 or Gray-100
            : (gridEnabled ? '#D1FAE5' : 'white'),    // Normal: Emerald-100 or White
        }}
        title={gridEnabled ? 'Raster ausblenden' : 'Raster einblenden'}
        aria-label={gridEnabled ? 'Raster ausblenden' : 'Raster einblenden'}
      >
        {gridEnabled ? (
          <Eye size={20} color="#10B981" strokeWidth={2} />
        ) : (
          <EyeOff size={20} color="#6B7280" strokeWidth={2} />
        )}
      </button>

      {/* Snap-to-grid toggle */}
      <button
        onClick={toggleSnap}
        onMouseEnter={() => setSnapButtonHover(true)}
        onMouseLeave={() => setSnapButtonHover(false)}
        style={{
          ...buttonBaseStyle,
          borderColor: snapEnabled ? '#10B981' : '#E5E7EB',  // Emerald-500 or Gray-200
          backgroundColor: snapButtonHover
            ? (snapEnabled ? '#A7F3D0' : '#F3F4F6')  // Hover: Emerald-200 or Gray-100
            : (snapEnabled ? '#D1FAE5' : 'white'),    // Normal: Emerald-100 or White
        }}
        title={snapEnabled ? 'Einrasten deaktivieren (Strg-Taste zum temporären Deaktivieren)' : 'Einrasten aktivieren'}
        aria-label={snapEnabled ? 'Einrasten deaktivieren' : 'Einrasten aktivieren'}
      >
        <Magnet
          size={20}
          color={snapEnabled ? '#10B981' : '#6B7280'}
          strokeWidth={2}
        />
      </button>

      {/* Grid size selector */}
      <select
        value={gridSize}
        onChange={(e) => setGridSize(Number(e.target.value))}
        style={{
          width: '66px',
          height: '44px',
          border: '1px solid #E5E7EB',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: '500',
          color: '#111827',
          padding: '0 8px 0 4px',
          outline: 'none',
        }}
        title="Rastergröße"
        aria-label="Rastergröße"
      >
        <option value={10}>10cm</option>
        <option value={25}>25cm</option>
        <option value={50}>50cm</option>
        <option value={100}>100cm</option>
      </select>

      {/* Helper text - shows current snap status */}
      <div
        style={{
          fontSize: '10px',
          color: snapEnabled ? '#6B7280' : 'transparent',
          textAlign: 'center',
          marginTop: '4px',
          whiteSpace: 'nowrap',
          minHeight: '14px', // Reserve space to prevent jumping
        }}
      >
        {snapEnabled ? 'Strg = Frei' : '\u00A0'}
      </div>
    </div>
  );
};

export default GridControls;
